import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { BrandLogo } from '../components/BrandLogo';

function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <BrandLogo className="w-9 h-9 sm:w-11 sm:h-11" testId="terms-logo" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Legacy Forge Family</h1>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <FaArrowLeft />
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-500 mb-8">Last updated: March 2025</p>

          <div className="prose prose-blue max-w-none space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing or using Legacy Forge Family ("the Service"), you agree to be bound by these 
                Terms of Service. If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">2. Description of Service</h2>
              <p>
                Legacy Forge Family is a family-oriented platform designed to help families connect, 
                preserve memories, plan reunions, manage family governance, and build lasting legacies 
                across generations. The Service includes features for:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Family group creation and management</li>
                <li>Photo and memory sharing</li>
                <li>Event planning and coordination</li>
                <li>Family messaging and communication</li>
                <li>Family timeline and history documentation</li>
                <li>Professional advisor connections</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">3. User Accounts</h2>
              <p>
                To use certain features of the Service, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Be responsible for all activities under your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">4. Family Groups & Invitations</h2>
              <p>
                Legacy Forge Family operates on an invitation-only basis for family groups. Family 
                owners and administrators are responsible for:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Inviting only authorized family members</li>
                <li>Managing member access and permissions</li>
                <li>Ensuring appropriate use within their family group</li>
                <li>Removing members who violate these terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">5. Acceptable Use</h2>
              <p>You agree not to use the Service to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Upload illegal, harmful, or offensive content</li>
                <li>Harass, threaten, or harm other users</li>
                <li>Impersonate others or misrepresent your identity</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Attempt to gain unauthorized access to the Service</li>
                <li>Use the Service for commercial purposes without authorization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">6. Content Ownership</h2>
              <p>
                You retain ownership of all content you upload to the Service, including photos, 
                messages, and family information. By uploading content, you grant Legacy Forge Family 
                a limited license to store, display, and process your content solely to provide the Service.
              </p>
              <p className="mt-2">
                You are responsible for ensuring you have the right to share any content you upload, 
                including obtaining consent from individuals in photographs.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">7. Subscription & Payments</h2>
              <p>
                Certain features require a paid subscription. By subscribing, you agree to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Pay all applicable fees for your chosen plan</li>
                <li>Provide valid payment information</li>
                <li>Accept automatic renewal unless cancelled</li>
              </ul>
              <p className="mt-2">
                Subscriptions may be cancelled at any time. Access continues until the end of the 
                current billing period. Refunds are provided in accordance with our refund policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">8. Data Retention</h2>
              <p>
                We understand your family data is precious. In the event of non-payment:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>A grace period will be provided before any feature restrictions</li>
                <li>Your data will be preserved and accessible in read-only mode</li>
                <li>We will never delete family data without significant advance notice</li>
                <li>Data export options are available upon request</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">9. Privacy</h2>
              <p>
                Your privacy is important to us. Please review our{' '}
                <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline">
                  Privacy Policy
                </Link>{' '}
                to understand how we collect, use, and protect your information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">10. Termination</h2>
              <p>
                We may suspend or terminate your access to the Service if you violate these terms 
                or engage in conduct that we determine is harmful to other users or the Service. 
                You may also delete your account at any time.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">11. Disclaimers</h2>
              <p>
                The Service is provided "as is" without warranties of any kind. While we strive to 
                maintain high availability and security, we cannot guarantee uninterrupted access 
                or complete protection against all threats.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">12. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Legacy Forge Family shall not be liable for 
                any indirect, incidental, special, or consequential damages arising from your use 
                of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">13. Changes to Terms</h2>
              <p>
                We may update these Terms of Service from time to time. We will notify users of 
                significant changes via email or through the Service. Continued use after changes 
                constitutes acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">14. Contact Us</h2>
              <p>
                If you have questions about these Terms of Service, please contact us through the 
                feedback button in the application or reach out to our support team.
              </p>
            </section>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-200">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <FaArrowLeft />
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 mt-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm">
            &copy; 2025 Legacy Forge Family. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default TermsOfService;
